import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';

import { Subscription, filter } from 'rxjs';
import moment from 'moment-timezone';
import * as _ from 'underscore';

import { UniqueIDService } from '../../../Misc/services/unique-id.service';
import { deliverySchedulingTypesEnum } from '../../enums/delivery-scheduling-types.enum';
import { TimeModel } from '../../models/time.model';
import { timeFormatsEnum } from '../../../../Core/Users/enums/time-formats.enum';
import { TwelveHourModel } from '../../models/twelve-hour.model';
import { TwentyFourHourModel } from '../../models/twenty-four-hour.model';
import { TimeHelper } from '../../helpers/time.helper';
import { MinuteModel } from '../../models/minute.model';
import { MessageModel } from '../../../Misc/models/message.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { DeliveryRecurrencePatternModel } from '../../models/delivery-recurrence-pattern.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { DeliveryRecurrenceFrequencyEnum } from '../../enums/delivery-recurrence-frequency.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';

export const deliverySchedulerComponentText = {
    selectADate: 'Select a date'
};

// Deprecated : Use DeliveryScheduleInput
@Component({
    selector: 'agpr-delivery-scheduler',
    templateUrl: './delivery-scheduler.component.html',
    styleUrls: ['./delivery-scheduler.component.scss'],
    animations: [
        trigger('containerState', [
            transition(':enter', [
                style({ opacity: 0, height: 0, overflow: 'hidden' }),
                animate('250ms ease-in', style({ opacity: 1, height: '*' }))
            ]),
            transition(':leave', [
                style({ opacity: 1, height: '*', overflow: 'hidden' }),
                animate('250ms ease-in', style({ opacity: 0, height: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class DeliverySchedulerComponent implements OnChanges, OnDestroy {
    public readonly messageSeverityEnum = messageSeverityEnum;

    @Input() localization = deliverySchedulerComponentText;
    @Input() inputId: string = null;
    @Input() minDate: Date = new Date();
    @Input() deliveryDate: Date = new Date();
    @Input() time: TimeModel = null;
    @Input() selectedSchedulingType: deliverySchedulingTypesEnum = deliverySchedulingTypesEnum.immediate;
    @Input() disabled: boolean = false;
    @Input() immediateOptionLabel: string = null;
    @Input() futureOptionLabel: string = null;
    @Input() minDateValidationMessage: string = null;
    @Input() infoIconStyleClass: string = null;
    @Input() infoTooltipStyleClass: string = null;
    @Input() messages: MessageModel[] = null;
    // Boolean for if we should show the recurring section or not, recurring section configured specifically for briefings requirements
    @Input() showRecurring: boolean = false;
    // Localization string for the recurring label
    @Input() recurringOptionLabel: string = null;
    @Input() disableSendNow: boolean = false;
    @Input() disableSendLater: boolean = false;
    @Input() disabledTooltip: string = '';

    // Input for the recurrencePatternModel, updates when the user updates the recurrence pattern
    @Input()
    get recurringModel(): DeliveryRecurrencePatternModel {
        return this.recurringModelValue;
    }
    set recurringModel(inputModel: DeliveryRecurrencePatternModel) {
        if (inputModel.times.length) {
            if (this.areMultipleTimeValuesInUse(inputModel.frequencyType)) {
                this.recurringMultiTimes = inputModel.times;
            } else {
                this.recurringTime = inputModel.times[0];
            }
        }

        this.recurringModelValue = inputModel;
    }

    @Output() deliveryDateChange: EventEmitter<Date> = new EventEmitter<Date>();
    @Output() timeChange: EventEmitter<TimeModel> = new EventEmitter<TimeModel>();
    @Output() selectedSchedulingTypeChange: EventEmitter<deliverySchedulingTypesEnum> = new EventEmitter<deliverySchedulingTypesEnum>();
    // Event emitter that gets updated everytime the user changes the recurrence pattern
    @Output() recurrencePatternChanged: EventEmitter<DeliveryRecurrencePatternModel> = new EventEmitter<DeliveryRecurrencePatternModel>();

    id: number = null;
    deliverySchedulingTypes = deliverySchedulingTypesEnum;
    calendarDate: Date = null;
    today: Date = new Date();
    isTooEarly: boolean = false;
    debouncedUpdateDeliveryDate = _.debounce(() => this.updateDeliveryDate(), 150);
    isRecurring: boolean = false;
    recurringMultiTimes: TimeModel[] = [
        new TimeModel()
    ];
    recurringTime: TimeModel = new TimeModel();
    daysOfTheWeek = DeliveryRecurrencePatternModel.DAYS_OF_THE_WEEK_OPTIONS;

    public readonly daysOfTheMonthOptions = DeliveryRecurrencePatternModel.DAYS_OF_THE_MONTH_OPTIONS;

    public deliveryRecurrenceFrequencyEnum = DeliveryRecurrenceFrequencyEnum;
    private subscriptions: Subscription[] = [];
    private recurringModelValue: DeliveryRecurrencePatternModel = new DeliveryRecurrencePatternModel();

    @ViewChild('calendar') calendar: any;

    errors: MessageModel[];
    timeFormat: timeFormatsEnum;
    /**
    * Class constructor
    */
    constructor(
        private uniqueIDService: UniqueIDService,
        private timeHelper: TimeHelper,
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService
    ) {

        this.id = uniqueIDService.generateID();
        this.selectedSchedulingType = deliverySchedulingTypesEnum.immediate;

        this.subscriptions.push(
            this.localizationService.getLocalizationMapChange().subscribe(
                localizationChange => {
                    this.daysOfTheWeek.forEach(
                        day => {
                            day.label = this.localizationService.transform(day.localizationKey);
                        }
                    );
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.timeFormat = prefs.timeFormat;
            })
        );
    }

    /**
    * Runs any time the component inputs change
    */
    ngOnChanges(changes: SimpleChanges): void {
        const deliveryDateIncluded: boolean = changes.deliveryDate !== null && changes.deliveryDate !== undefined;
        const hasCurrentDate: boolean = deliveryDateIncluded && changes.deliveryDate.currentValue instanceof Date;
        const hasPreviousDate: boolean = deliveryDateIncluded && changes.deliveryDate.previousValue instanceof Date;
        const previousDateWasEmpty: boolean = deliveryDateIncluded && changes.deliveryDate.previousValue == null;

        if (deliveryDateIncluded) {
            const newDeliveryDate: Date = changes.deliveryDate.currentValue;

            if (hasCurrentDate && hasPreviousDate) {
                const currentTimeStamp: number = changes.deliveryDate.currentValue.getTime();
                const previousTimeStamp: number = changes.deliveryDate.previousValue.getTime();
                const deliveryDateChanged: boolean = deliveryDateIncluded &&  currentTimeStamp !== previousTimeStamp;

                if (deliveryDateChanged) {
                    this.onDeliveryDateChange(newDeliveryDate);
                }
            } else if (previousDateWasEmpty && hasCurrentDate) {
                this.onDeliveryDateChange(newDeliveryDate);
            }
        }
        this.errors = this.getErrorMessages();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    /**
    * Updates the time portion of the delivery date
    */
    updateDeliveryDate(): void {
        if (this.selectedSchedulingType !== deliverySchedulingTypesEnum.future) {
            // if the timezone gets auto-updated, then don't send changes
            return;
        }

        // Moment only accepts 24 hour formatted hours, so the format is converted
        const deliveryDate: Date = this.deliveryDate || this.calendarDate;
        let twentyFourHourTime;
        if (this.time.format !== timeFormatsEnum.twentyFourHour) {
            twentyFourHourTime = this.time.changeFormat(timeFormatsEnum.twentyFourHour);
        } else {
            twentyFourHourTime = this.time;
        }

        const month: number = moment(this.calendarDate).month();
        const day: number = moment(this.calendarDate).date();
        const year: number = moment(this.calendarDate).year();
        const hour: TwelveHourModel | TwentyFourHourModel = twentyFourHourTime.hour;
        const minute: MinuteModel = this.time.minute;
        let newDeliveryDate: moment.Moment;
        this.timeChange.emit(this.time);

        if (deliveryDate && this.calendarDate instanceof Date) {
            newDeliveryDate = moment(deliveryDate);

            // Set the new time zone
            newDeliveryDate.tz(this.time.timeZone);

            // Add the time in the new time zone
            newDeliveryDate.year(year);
            newDeliveryDate.month(month);
            newDeliveryDate.date(day);
            newDeliveryDate.hour(hour);
            newDeliveryDate.minute(minute);

            // Set the new date
            this.deliveryDateChange.emit(newDeliveryDate.toDate());
        }

        this.errors = this.getErrorMessages();
    }

    /**
    * Transfer the times selected to the structure for saved information to be retained
    */
    updateRecurringPattern(): void {
        if (this.areMultipleTimeValuesInUse()) {
            this.recurringModel.times = this.recurringMultiTimes.slice();
            this.recurringModel.timeZone = this.recurringMultiTimes[0].timeZone;
        } else {
            this.recurringModel.times = [this.recurringTime];
            this.recurringModel.timeZone = this.recurringTime.timeZone;
        }
        this.recurrencePatternChanged.emit(this.recurringModel);
        this.errors = this.getErrorMessages();
    }

    /**
    * Called when the recurring type changes, emits event for parent to react to
    */
    updateRecurringType(type: string): void {
        if (type === this.recurringModel.frequencyType) {
            return;
        }
        this.recurringModel.frequencyType = type;

        if (type === DeliveryRecurrenceFrequencyEnum.quarterly) {
            // Quarterly publishes the day after the end of each quarter at noon; only the timezone
            // is user-selectable, so we lock the time to noon and keep whatever timezone was in use.
            this.recurringTime = new TimeModel(
                timeFormatsEnum.twentyFourHour,
                12 as TwentyFourHourModel,
                0 as MinuteModel,
                null,
                this.recurringTime?.timeZone ?? this.recurringModel.timeZone
            );
            this.recurringModel.timeZone = this.recurringTime.timeZone;
        }

        if (this.areMultipleTimeValuesInUse()) {
            this.recurringModel.times = [
                this.timeHelper.changeFormat(
                    this.recurringMultiTimes[0],
                    timeFormatsEnum.twentyFourHour
                )
            ];
        } else {
            this.recurringModel.times = [
                this.timeHelper.changeFormat(
                    this.recurringTime,
                    timeFormatsEnum.twentyFourHour
                )
            ];
        }

        if (this.recurringModel.frequencyType === DeliveryRecurrenceFrequencyEnum.daily) {
            // For daily, choose all the days of the week
            this.recurringModel.frequencyValues = [0, 1, 2, 3, 4, 5, 6];
        } else {
            this.recurringModel.frequencyValues = [];
        }

        this.recurrencePatternChanged.emit(this.recurringModel);
        this.errors = this.getErrorMessages();
    }

    /**
    * Responds to changes in the scheduling type
    */
    schedulingTypeChanged(newSchedulingType: deliverySchedulingTypesEnum): void {
        switch (newSchedulingType) {
            case deliverySchedulingTypesEnum.immediate:
                this.setImmediateDate();
                this.isRecurring = false;
                break;
            case deliverySchedulingTypesEnum.recurring:
                this.isRecurring = true;

                if (this.areMultipleTimeValuesInUse()) {
                    this.recurringMultiTimes[0] = this.timeHelper.getTimeFromDate(
                        new Date(),
                        this.recurringModel.timeZone,
                        this.timeFormat
                    );
                } else {
                    this.recurringTime = this.timeHelper.getTimeFromDate(
                        new Date(),
                        this.recurringModel.timeZone,
                        this.timeFormat
                    );
                    this.recurringMultiTimes = [this.recurringTime];
                }

                this.updateRecurringType(DeliveryRecurrenceFrequencyEnum.daily);
                break;
            default:
                this.isRecurring = false;
        }

        this.selectedSchedulingTypeChange.emit(newSchedulingType);
        this.errors = this.getErrorMessages();
    }

    /**
    * Returns a boolean representing whether the future date selector should be enabled or not
    */
    isFutureDateSelectorEnabled(): boolean {
        return this.minDate > this.today || this.selectedSchedulingType === deliverySchedulingTypesEnum.future;
    }

    /**
    * Gets a list of error messages based on messages passed from outside of the component
    * and the UI state in the component
    */
    getErrorMessages(): MessageModel[] {
        let messages: MessageModel[] = [];
        let tooEarlyMessage: MessageModel = null;

        if (this.messages) {
            messages = this.messages.filter(message => message.severity === messageSeverityEnum.error);
        }

        if (!this.isRecurring && this.isTooEarly && this.minDateValidationMessage) {
            tooEarlyMessage = {
                severity: messageSeverityEnum.error,
                summary: this.minDateValidationMessage
            };

            messages.push(tooEarlyMessage);
        }

        return messages;
    }

    /**
    * Opens the delivery date calendar
    */
    openCalendar(event: Event) {
        this.calendar.showOverlay();

        event.stopPropagation();
    }

    /**
    * React to changes in the delivery date by updating other fields and
    * validating against the min date
    */
    private onDeliveryDateChange(newDate: Date): void {
        const hasValidHour = this.time && this.time.hour !== null && this.time.hour !== undefined;
        const hasValidMinute = this.time && this.time.minute !== null && this.time.minute !== undefined;

        this.updateCalendarFromDeliveryDate(newDate);
        this.validateByMinDate();

        if (!hasValidHour || !hasValidMinute) {
            this.updateTimeFromDate();
        }
    }

    /**
    * Sets the delivery date to the current date/time
    */
    private setImmediateDate(): void {
        this.time.hour = null;
        this.time.minute = null;

        this.deliveryDate = new Date();
        this.deliveryDateChange.emit(this.deliveryDate);
    }

    /**
    * Creates a date object from the delivery date, but where
    * the month and day will reflect the time in the selected timezone
    * instead of the time zone of the user's computer
    */
    private updateCalendarFromDeliveryDate(deliveryDate: Date): void {
        if (deliveryDate) {
            const deliveryDateMoment: moment.Moment = moment(deliveryDate);
            const calendarDateMoment: moment.Moment = moment(new Date());

            // Set up the delivery date to work in the selected timezone
            deliveryDateMoment.tz(this.time.timeZone);

            // Get the month and day from
            // the delivery date in the selected timezone
            const month = deliveryDateMoment.month();
            const day = deliveryDateMoment.date();
            const year = deliveryDateMoment.year();

            // Set the calendar's date to display the month and day
            // in the correct timezone
            calendarDateMoment.month(month);
            calendarDateMoment.date(day);
            calendarDateMoment.year(year);

            this.calendarDate = calendarDateMoment.toDate();
        } else {
            this.calendarDate = null;
        }
    }

    /**
     * Updates any missing fields in the time based on the time settings in the delivery date
     */
    private updateTimeFromDate(): void {
        if (this.deliveryDate) {
            this.time = this.timeHelper.getTimeFromDate(this.deliveryDate, this.time.timeZone, this.time.format);
        }
    }

    /**
     * Checks if the selected date is greater than the minimum date,
     * and sets a flag based on the result
     */
    private validateByMinDate(): void {
        this.isTooEarly = this.deliveryDate < this.minDate;
    }

    /**
     * Determine if the internal structure to store time values are an array or not, based upon the recurring frequency
     *
     * Currently only Daily recurring types use an array for times - update this logic if other frequencies allow
     * for multiple time values.
     *
     * @param frequencyType
     */
    private areMultipleTimeValuesInUse(frequencyType: string = null): boolean {
        if (frequencyType === null) {
            return this.recurringModel.frequencyType === DeliveryRecurrenceFrequencyEnum.daily;
        } else {
            return frequencyType === DeliveryRecurrenceFrequencyEnum.daily;
        }
    }
}
